import type { AxiosProgressEvent } from 'axios';
import type { IPaginatedResponse } from '../../types';
import type { TBillingPayment } from '../BillingService/types';
import type { Organization } from '../CustomerService/organization.types';

export enum ColumnIDN {
	NewLead = 'new_lead',
	OnHold = 'on_hold',
	Contacting = 'contacting',
	DemoScheduled = 'demo_scheduled',
	DemoCompleted = 'demo_completed',
	PaymentAttached = 'payment_attached',
	Implementation = 'implementation',
	ReadyForProduction = 'ready_for_production',
	AtRisk = 'at_risk',
	Expand = 'expand',
	Maintain = 'maintain',
	Lost = 'lost'
}

export enum ContractAccountType {
	Current = 'current',
	Attainment = 'attainment'
}

export enum ContractTriggerType {
	PipelineStageIsOver = 'pipeline_stage_is_over',
	Monthly = 'monthly',
	SubscriptionPayment = 'subscription_payment'
}

export enum ContractRewardType {
	Const = 'const',
	Calculated = 'calculated'
}

export type Pipeline = {
	id: string;
	idn: string;
	title: string;
};

export type ContractRewardResponse = {
	id: string;
	title: string;
	idn: string;
	reward_trigger_type: ContractTriggerType;
	pipeline_stage_id: string | null;
	type: ContractRewardType;
	value_func: string | null;
	value: number;
	account_type: ContractAccountType | null;
	recipient_idn: string | null;
	payer_idn: string;
	reward_owner_partner_idn: string | null;
	created_at: string;
	recipient_payment_rate: number | null;
};

export type ContractReward = {
	accountType: ContractAccountType | null;
	createdAt: string;
	id: string;
	idn: string;
	payerIdn: string;
	pipelineStageId: string | null;
	recipientIdn: string | null;
	recipientPaymentRate: number | null;
	rewardOwnerPartnerIdn: string | null;
	rewardTriggerType: ContractTriggerType;
	title: string;
	type: ContractRewardType;
	value: number;
	valueFunc: string | null;
};

export type ContractResponse = {
	id: string;
	partner_idn: string;
	contract_owner_partner_idn: string | null;
	owner_partner_idn?: string | null;
	account_type: ContractAccountType;
	contract_trigger_type?: ContractTriggerType | null;
	contact_trigger_type?: ContractTriggerType | null;
	trigger_type?: ContractTriggerType | null;
	pipeline_stage_id: string | null;
	start_date: string | null;
	end_date: string | null;
	max_reward_case_count: number | null;
	contract_payout_cap: number | null;
	contract_payout_floor: number | null;
	reward_case_duration: number | null;
	contract_case_duration?: number | null;
	contract_case_payout_cap: number | null;
	contract_case_payout_floor: number | null;
	recipient_idn: string | null;
	created_at: string;
	updated_at: string | null;
	rewards?: ContractRewardResponse[] | null;
};

export type Contract = {
	accountType: ContractAccountType;
	contractCasePayoutCap: number | null;
	contractCasePayoutFloor: number | null;
	contractOwnerPartnerIdn: string | null;
	contractPayoutCap: number | null;
	contractPayoutFloor: number | null;
	contractTriggerType: ContractTriggerType | null;
	createdAt: string;
	endDate: string | null;
	id: string;
	maxRewardCaseCount: number | null;
	partnerIdn: string;
	pipelineStageId: string | null;
	recipientIdn: string | null;
	rewardCaseDuration: number | null;
	rewards?: ContractReward[] | null;
	startDate: string | null;
	updatedAt: string;
};

export type ContractCaseResponse = {
	id: string;
	contract_id: string;
	partner_idn: string;
	recipient_idn: string | null;
	contract_case_duration: number | null;
	is_active: boolean;
	contract_case_payout_cap: number | null;
	contract_case_payout_floor: number | null;
	created_at: string;
	pipeline_record_id: string;
};

export type ContractCaseWithRewardsResponse = ContractCaseResponse & {
	rewards: ContractRewardResponse[];
};

export type ContractCaseWithRewards = {
	contractCaseDuration: number | null;
	contractCasePayoutCap: number | null;
	contractCasePayoutFloor: number | null;
	contractId: string;
	createdAt: string;
	id: string;
	isActive: boolean;
	partnerIdn: string;
	pipelineRecordId: string;
	recipientIdn: string | null;
	rewards: ContractReward[];
};

export interface PipelineRecordCustomer {
	id: Organization['id'];
	organization_name: Organization['organization_name'];
	agent_name: Organization['agent_name'];
	agent_voice_phone_number: Organization['agent_voice_phone_number'];
	logo: Organization['logo'];
	session_count: NonNullable<Organization['session_count']>;
	phone_number: Organization['phone_number'];
	industry: string | null;
	account_role: Organization['account_role'];
	subscription: Organization['subscription'];
	created_at: Organization['created_at'];
	last_session_datetime?: Organization['last_session_datetime'];
	is_initialized: Organization['is_initialized'];
	source?: Organization['source'];
}

export enum PipelineRecordPayloadFieldIDN {
	Comment = 'comment',
	DemoDate = 'demo_date',
	DemoSummary = 'demo_summary',
	Email = 'email',
	ExpansionValue = 'expansion_value',
	FilledBoi = 'filled_boi',
	FilledDeclaration = 'filled_declaration',
	FullName = 'full_name',
	Industry = 'industry',
	Logo = 'logo',
	Logotype = 'logotype',
	NextContactDate = 'next_contact_date',
	NoShow = 'no_show',
	Notes = 'notes',
	OrganizationCustomerIdn = 'organization_customer_idn',
	OrganizationName = 'organization_name',
	OrganizationWebsite = 'organization_website',
	PartnerAssigneeIdn = 'partner_assignee_idn',
	PartnerCustomerIdn = 'partner_customer_idn',
	PartnerMemberAssigneeIdn = 'partner_member_assignee_idn',
	PhoneNumber = 'phone_number',
	PipelineIdn = 'pipeline_idn',
	ProductionStartDate = 'production_start_date',
	Reason = 'reason',
	StageIdn = 'stage_idn',
	SubscriptionPaymentLink = 'subscription_payment_link',
	VideoRecordLink = 'video_record_link',
	LeadValue = 'value'
}

export type AssigneeAccount = {
	idn: string;
	name: string;
	email: string;
	avatar: string;
};

export const PipelineRecordAccessReason = {
	AnotherPartnerRecord: 'another_partner_record',
	NoStageAccess: 'no_stage_access',
	NotPartnerEmployee: 'not_partner_employee'
} as const;

export type PipelineRecordAccessReasonIdn =
	(typeof PipelineRecordAccessReason)[keyof typeof PipelineRecordAccessReason];

export type PipelineRecordAccessRight =
	| {
			read_only: false;
			reason: null;
	  }
	| {
			read_only: true;
			reason: PipelineRecordAccessReasonIdn;
	  };

export type PipelineRecord = {
	id: string;
	pipeline_idn: string;
	stage_idn: ColumnIDN;
	partner_member_assignee_idn: string | null;
	stage_changed_at: string | null;
	assignee_account: AssigneeAccount | null;
	organization_customer_idn: string | null;
	organization_customer_id: string | null;
	organization_name: string | null;
	full_name: string | null;
	email?: string | null;
	phone?: string | null;
	phone_number?: string | null;
	logo: string | null;
	logotype?: string | null;
	website?: string | null;
	organization_website?: string | null;
	demo_date: string | null;
	demo_summary?: string | null;
	filled_boi?: string | null;
	filled_declaration?: string | null;
	last_session_datetime: string | null;
	next_contact_date?: string | null;
	no_show?: string | null;
	notes?: string | null;
	production_start_date?: string | null;
	reason?: string | null;
	subscription_payment_link?: string | null;
	video_record_link?: string | null;
	value: number;
	partner_customer_idn: string;
	primary_partner_idn: string;
	expansion_value: number;
	comment: string | null;
	industry: string | null;
	created_at: string;
	updated_at: string | null;
	updated_by: string | null;
	is_blocked?: boolean;
	session_count: number;
	customer: PipelineRecordCustomer | null;
	access_right?: PipelineRecordAccessRight | null;
};

export type PipelineRecordFile = {
	id: string;
	field_idn: string | null;
	stage_idn?: ColumnIDN | null;
	filename: string;
	content_type: string;
	size_bytes: number;
	uploaded_by_account_id: string;
	created_at: string;
	download_url: string;
	download_url_expires_at: string;
};

export type PipelineFieldValue = {
	idn: string;
	value: string;
};

export type CrmPipelineStageFieldDefinition = {
	format?: 'iso-date';
	required: boolean;
	type: 'text';
};

export type CrmPipelineStageFields = Partial<
	Record<PipelineRecordPayloadFieldIDN, CrmPipelineStageFieldDefinition>
>;

export const CrmPipelineReasonNextContactStageFields: CrmPipelineStageFields = {
	[PipelineRecordPayloadFieldIDN.Reason]: {
		required: false,
		type: 'text'
	},
	[PipelineRecordPayloadFieldIDN.NextContactDate]: {
		format: 'iso-date',
		required: false,
		type: 'text'
	},
	[PipelineRecordPayloadFieldIDN.Comment]: {
		required: false,
		type: 'text'
	}
};

export const CrmPipelineStageMapper: Record<ColumnIDN, CrmPipelineStageFields> =
	{
		[ColumnIDN.NewLead]: {
			[PipelineRecordPayloadFieldIDN.Reason]: {
				required: false,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.Comment]: {
				required: false,
				type: 'text'
			}
		},
		[ColumnIDN.OnHold]: {
			...CrmPipelineReasonNextContactStageFields
		},
		[ColumnIDN.Contacting]: {
			[PipelineRecordPayloadFieldIDN.FullName]: {
				required: true,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.Email]: {
				required: true,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.PhoneNumber]: {
				required: true,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.OrganizationName]: {
				required: true,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.OrganizationWebsite]: {
				required: true,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.Logo]: {
				required: false,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.Logotype]: {
				required: false,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.Comment]: {
				required: false,
				type: 'text'
			}
		},
		[ColumnIDN.DemoScheduled]: {
			[PipelineRecordPayloadFieldIDN.DemoDate]: {
				format: 'iso-date',
				required: true,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.Comment]: {
				required: false,
				type: 'text'
			}
		},
		[ColumnIDN.DemoCompleted]: {
			[PipelineRecordPayloadFieldIDN.DemoSummary]: {
				required: true,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.VideoRecordLink]: {
				required: false,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.NoShow]: {
				required: false,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.Comment]: {
				required: false,
				type: 'text'
			}
		},
		[ColumnIDN.PaymentAttached]: {
			[PipelineRecordPayloadFieldIDN.SubscriptionPaymentLink]: {
				required: false,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.Comment]: {
				required: false,
				type: 'text'
			}
		},
		[ColumnIDN.Implementation]: {
			[PipelineRecordPayloadFieldIDN.FilledBoi]: {
				required: true,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.FilledDeclaration]: {
				required: true,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.Comment]: {
				required: false,
				type: 'text'
			}
		},
		[ColumnIDN.ReadyForProduction]: {
			[PipelineRecordPayloadFieldIDN.ProductionStartDate]: {
				format: 'iso-date',
				required: true,
				type: 'text'
			},
			[PipelineRecordPayloadFieldIDN.Comment]: {
				required: false,
				type: 'text'
			}
		},
		[ColumnIDN.AtRisk]: {
			...CrmPipelineReasonNextContactStageFields
		},
		[ColumnIDN.Expand]: {
			[PipelineRecordPayloadFieldIDN.Comment]: {
				required: false,
				type: 'text'
			}
		},
		[ColumnIDN.Maintain]: {
			[PipelineRecordPayloadFieldIDN.Comment]: {
				required: false,
				type: 'text'
			}
		},
		[ColumnIDN.Lost]: {
			...CrmPipelineReasonNextContactStageFields
		}
	};

export type PipelineRecordFieldUpdate = PipelineFieldValue & {
	stageIdn?: ColumnIDN;
};

export type PipelineRecordHistoryStageFieldValue = PipelineFieldValue & {
	title: string;
	is_optional?: boolean | null;
};

export type PipelineRecordHistoryStageFile = PipelineRecordFile;

export type PipelineRecordImplementationFileFieldIDN =
	| PipelineRecordPayloadFieldIDN.FilledBoi
	| PipelineRecordPayloadFieldIDN.FilledDeclaration;

export type PipelineRecordImplementationFiles = Partial<
	Record<
		PipelineRecordImplementationFileFieldIDN,
		PipelineRecordHistoryStageFile[]
	>
>;

export type PipelineRecordHistoryStage = {
	id: string;
	stage_idn: ColumnIDN;
	stage_title: string;
	fields: PipelineRecordHistoryStageFieldValue[];
	files?: PipelineRecordHistoryStageFile[];
	created_at: string;
	completed_at: string | null;
	completed_by_partner_assignee_idn: string | null;
	completed_by_partner_member_assignee_idn: string | null;
	completed_by_partner_member_assignee: AssigneeAccount | null;
};

export type PipelineRecordPaymentHistoryItem = TBillingPayment & {
	amount_refunded: number;
	refunded: boolean;
};

export type PipelineRecordDetail = PipelineRecord & {
	docs?: PipelineRecordFile[];
	history: PipelineRecordHistoryStage[];
	implementation_files?: PipelineRecordImplementationFiles;
	payment_history?: PipelineRecordPaymentHistoryItem[] | null;
};

export type CRMGroupedColumnData = {
	items: PipelineRecord[];
	total: number;
	has_more: boolean;
	aggregates: {
		critical_count: number;
		warning_count: number;
		total_lead_value: number;
		total_expansion_value: number;
	};
};

export type CrmGroupedResponse =
	| {
			groups: Partial<Record<ColumnIDN, CRMGroupedColumnData>>;
	  }
	| CRMGroupedColumnData;

export type CrmPipelineRecordsGroupBy = 'stage_idn';

export type GetCrmGroupedRequest = {
	pipeline_idn: string;
	stage_idns: ColumnIDN[];
	group_by?: CrmPipelineRecordsGroupBy;
	search?: string;
	sort_by?: string;
	partner_member_assignee_idn?: string;
	organization_name?: string;
	primary_contact_name?: string;
	phone_number?: string;
	email?: string;
	priorities?: string[];
	unassigned?: boolean;
	is_blocked?: boolean;
	page?: number;
	per?: number;
};

export type CreatePipelineRecordRequest = {
	pipeline_idn: string;
	stage_idn: ColumnIDN;
	partner_customer_idn: string;
	value?: number | string;
	expansion_value?: number | string;
	organization_customer_idn?: string;
	partner_member_assignee_idn?: string;
	industry?: string;
	fieldUpdates?: PipelineRecordFieldUpdate[];
};

export type PipelineRecordSyncScalarValue = string | number | null;

export type PipelineRecordSyncStageValue = Record<
	string,
	PipelineRecordSyncScalarValue
>;

export type PipelineRecordSyncValue =
	| PipelineRecordSyncScalarValue
	| PipelineRecordSyncStageValue;

export type UpdatePipelineRecordPayload = {
	value?: number | string | null;
	expansion_value?: number | string | null;
	comment?: string | PipelineRecordSyncStageValue | null;
	partner_member_assignee_idn?: string | null;
	partner_assignee_idn?: string | null;
	organization_customer_idn?: string | null;
	stage_idn?: ColumnIDN | null;
	[fieldIdn: string]: PipelineRecordSyncValue | undefined;
};

export type CreatePipelineRecordPayload = Omit<
	CreatePipelineRecordRequest,
	'fieldUpdates'
> &
	UpdatePipelineRecordPayload;

export type UpdatePipelineRecordRequest = {
	fieldUpdates?: PipelineRecordFieldUpdate[];
	recordId: string;
	stageIdn?: ColumnIDN;
	payload: UpdatePipelineRecordPayload;
};

export type UploadPipelineRecordFileRequest = {
	recordId: string;
	file: File;
	fieldIdn?: PipelineRecordPayloadFieldIDN | null;
	stageIdn?: ColumnIDN | null;
	onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
};

export type PipelineRecordTransitionMissingField = {
	stage_idn: ColumnIDN;
	field_idn: string;
};

export enum PipelineRecordTransitionBlockerType {
	PaymentRequired = 'payment_required',
	ContractorUnavailable = 'contractor_unavailable',
	AssigneeRequired = 'assignee_required',
	FilesRequired = 'files_required',
	BackwardNotAllowed = 'backward_not_allowed'
}

export type PipelineRecordTransitionBlocker = {
	type: PipelineRecordTransitionBlockerType;
};

export type PipelineRecordTransitionOutcome = {
	requested_stage_idn: ColumnIDN;
	happened: boolean;
	stages_completed: ColumnIDN[];
	missing_fields: PipelineRecordTransitionMissingField[];
	blockers: PipelineRecordTransitionBlocker[];
};

export type UpdatePipelineRecordResponse = PipelineRecordDetail & {
	transition: PipelineRecordTransitionOutcome | null;
};

export type CreateAgentRequest = {
	pipeline_record_id: string;
	email: string;
	full_name?: string | null;
	phone_number?: string | null;
	organization_name?: string | null;
	organization_website?: string | null;
	logo?: string | null;
	init?: boolean;
};

export type CreateAgentResponse = {
	actor_id: string;
	name: string;
	provisioning_id: string;
	external_event_id: string;
};

export type GetAgentResponse = {
	id: string;
	actor_id: string;
	provisioning_id: string;
	external_event_id: string;
	customer_idn?: string;
};

export type PartnerEmployee = {
	customer_id: string;
	account_id: string;
	available_stage_idns: ColumnIDN[];
	idn: string;
	email: string;
	name: string;
	avatar: string | null;
	created_at: string;
	accepted_at: string | null;
	pipeline_records_count: number;
};

export type PartnerEmployeesRequest = {
	page: number;
	per: number;
	search?: string;
	available_stage_idns?: string[];
	tenant?: string;
};

export type PartnerEmployeesResponse = IPaginatedResponse<PartnerEmployee>;

export enum CRMActivityType {
	AssigneeChanged = 'assignee_changed',
	CommentAdded = 'comment_added',
	FieldChanged = 'field_changed',
	FileAdded = 'file_added',
	PartnerOrganizationChanged = 'partner_organization_changed',
	RecordUpdated = 'record_updated',
	StageTransitioned = 'stage_transitioned'
}

export type CRMActivityEmployeeValue = {
	avatar_url: string | null;
	full_name: string | null;
	idn: string;
};

export type CRMActivityPartnerOrganizationValue = {
	idn: string;
};

export type CRMActivityFieldValue =
	| number
	| string
	| CRMActivityEmployeeValue
	| CRMActivityPartnerOrganizationValue
	| null;

export type CRMActivityBaseChange = {
	content_type?: string | null;
	field_idn?: PipelineRecordPayloadFieldIDN | string | null;
	file_id?: string | null;
	filename?: string | null;
	new_value?: CRMActivityFieldValue;
	old_value?: CRMActivityFieldValue;
	size_bytes?: number | null;
	stage_idn?: string | null;
	text?: string | null;
};

export type CRMActivityAssigneeChange = CRMActivityBaseChange & {
	field_idn: PipelineRecordPayloadFieldIDN | string;
	new_value: CRMActivityEmployeeValue | null;
	old_value: CRMActivityEmployeeValue | null;
	type: CRMActivityType.AssigneeChanged;
};

export type CRMActivityCommentChange = CRMActivityBaseChange & {
	field_idn: PipelineRecordPayloadFieldIDN | string;
	new_value?: undefined;
	old_value?: undefined;
	text: string;
	type: CRMActivityType.CommentAdded;
};

export type CRMActivityFieldChange = CRMActivityBaseChange & {
	field_idn: PipelineRecordPayloadFieldIDN | string;
	new_value: number | string | null;
	old_value: number | string | null;
	type: CRMActivityType.FieldChanged;
};

export type CRMActivityFileChange = CRMActivityBaseChange & {
	content_type: string;
	field_idn?: PipelineRecordPayloadFieldIDN | string | null;
	file_id: string;
	filename: string;
	new_value?: undefined;
	old_value?: undefined;
	size_bytes: number;
	type: CRMActivityType.FileAdded;
};

export type CRMActivityPartnerOrganizationChange = CRMActivityBaseChange & {
	field_idn: PipelineRecordPayloadFieldIDN | string;
	new_value: CRMActivityPartnerOrganizationValue | null;
	old_value: CRMActivityPartnerOrganizationValue | null;
	type: CRMActivityType.PartnerOrganizationChanged;
};

export type CRMActivityStageTransitionChange = CRMActivityBaseChange & {
	field_idn: PipelineRecordPayloadFieldIDN | string;
	new_value: string;
	old_value: string;
	type: CRMActivityType.StageTransitioned;
};

export type CRMActivityChange =
	| CRMActivityAssigneeChange
	| CRMActivityCommentChange
	| CRMActivityFieldChange
	| CRMActivityFileChange
	| CRMActivityPartnerOrganizationChange
	| CRMActivityStageTransitionChange;

export type CRMActivity = {
	actor: PartnerEmployee | null;
	changes: CRMActivityChange[];
	created_at: string;
	id: string;
	pipeline_record_id: string;
	type: CRMActivityType;
};

export type CRMActivitiesRequest = {
	activity_types?: CRMActivityType[];
	actor_idns?: string[];
	created_from?: string;
	created_to?: string;
	field_idns?: string[];
	page: number;
	per: number;
	recordId: string;
};

export type CRMActivitiesResponse = IPaginatedResponse<CRMActivity>;

export type CreatePartnerEmployee = {
	email: string;
	available_stage_idns?: string[];
	name?: string;
	tenant?: string;
};

export type CreatePartnerEmployeeResult = PartnerEmployee;

export type UpdatePartnerEmployee = {
	targetAccountId: string;
	available_stage_idns: string[];
};

export const CRMPipelineRecordTaskStatus = {
	Active: 'active',
	Completed: 'completed',
	Canceled: 'canceled'
} as const;

export type CRMPipelineRecordTaskStatusValue =
	(typeof CRMPipelineRecordTaskStatus)[keyof typeof CRMPipelineRecordTaskStatus];

export type CRMPipelineRecordTask = {
	id: string;
	pipeline_record_id: string;
	summary: string;
	details: string | null;
	due_at: string;
	assignee: {
		idn: string;
		full_name: string | null;
		email: string;
		avatar_url?: string | null;
	};
	status: CRMPipelineRecordTaskStatusValue;
	created_at: string;
	updated_at: string;
	completed_at: string | null;
	canceled_at: string | null;
	reopened_at: string | null;
};

export type CRMPipelineRecordTaskGroup = {
	items: CRMPipelineRecordTask[];
	total: number;
};

export type CRMPipelineRecordTasksGroupedResponse = {
	groups: Record<CRMPipelineRecordTaskStatusValue, CRMPipelineRecordTaskGroup>;
};

export type CreateCRMPipelineRecordTaskPayload = {
	summary: string;
	details?: string | null;
	due_at: string;
	assignee_idn: string;
};

export type CreateCRMPipelineRecordTaskRequest =
	CreateCRMPipelineRecordTaskPayload & {
		recordId: string;
	};

export type UpdateCRMPipelineRecordTaskPayload = {
	summary?: string;
	details?: string | null;
	due_at?: string;
	assignee_idn?: string;
	status?: CRMPipelineRecordTaskStatusValue;
};

export type UpdateCRMPipelineRecordTaskRequest =
	UpdateCRMPipelineRecordTaskPayload & {
		recordId: string;
		taskId: string;
	};

export type DeleteCRMPipelineRecordTaskRequest = {
	recordId: string;
	taskId: string;
};
